import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { AgentRecordDto } from '../../dtos/agent-record.dto';
import { AgentActionEnum } from '../../enums/agent-action.enum';
import { AgentStatusEnum } from '../../enums/agent-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
  record: AgentRecordDto,
  action: AgentActionEnum,
  abortEarly = false,
): ValidationFunctionResult => {
  // Check if record is provided
  if (!record) throw new Error('record is required.');

  let validationResult: ValidationResult | undefined;

  const fixedStatuses = [
    AgentStatusEnum.ACTIVE,
    AgentStatusEnum.CLOSED,
    AgentStatusEnum.ON_HOLD,
  ];

  switch (action) {
    case AgentActionEnum.ACTIVATE:
      validationResult = setStatusSchema(
        AgentStatusEnum.ACTIVE,
        fixedStatuses,
      ).validate(record, {
        abortEarly,
      });
      break;

    case AgentActionEnum.CLOSE:
      validationResult = setStatusSchema(
        AgentStatusEnum.CLOSED,
        fixedStatuses,
      ).validate(record, {
        abortEarly,
      });
      break;

    case AgentActionEnum.PUT_ON_HOLD:
      validationResult = setStatusSchema(
        AgentStatusEnum.ON_HOLD,
        fixedStatuses,
      ).validate(record, {
        abortEarly,
      });
      break;

    default:
      throw new Error(`Unknown action: ${action}`);
  }

  // Check for validation errors
  if (validationResult?.error) {
    return {
      isValid: false,
      errors: validationResult.error.details?.map((e) => e.message),
    };
  }

  // Return valid result if no errors
  return { isValid: true };
};
